import React from "react";
import { isChunkError } from "@/lib/isChunkError";
import { ChunkErrorScreen } from "./ChunkErrorScreen";

interface State {
  error: unknown;
}

export class ChunkErrorBoundary extends React.Component<
  { children: React.ReactNode },
  State
> {
  state: State = { error: null };

  static getDerivedStateFromError(error: unknown): State {
    return { error };
  }

  componentDidCatch(error: unknown) {
    if (isChunkError(error)) {
      console.warn("[Fennest] Chunk load failed after retry, showing fallback");
    }
  }

  handleReload = async () => {
    try {
      sessionStorage.removeItem("fn_chunk_retry");
    } catch {
      /* ignore */
    }
    try {
      if ("serviceWorker" in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister().catch(() => {})));
      }
    } catch {
      /* ignore */
    }
    try {
      if ("caches" in window) {
        const names = await caches.keys();
        await Promise.all(names.map((n) => caches.delete(n)));
      }
    } catch {
      /* ignore */
    }
    try {
      const url = new URL(window.location.href);
      url.searchParams.set("_r", Date.now().toString());
      window.location.replace(url.toString());
    } catch {
      window.location.reload();
    }
  };

  render() {
    if (this.state.error) {
      if (isChunkError(this.state.error)) {
        return <ChunkErrorScreen onReload={this.handleReload} />;
      }
      // Not our concern - rethrow so other boundaries / dev overlay can handle it.
      throw this.state.error;
    }
    return this.props.children;
  }
}
